import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import DataSource from 'devextreme/data/data_source';
import ValidationEngine from 'devextreme/ui/validation_engine';
import { BehaviorSubject, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { FusionAuthService } from '../../../../../shared/services/fusion-auth';
import { DateHelperService, ScreenService } from '../../../../../shared/services/general';
import { BillingInformation } from '../../../../shared/models/billing/billing-information';
import { ZipCode } from '../../../../shared/models/common';
import { ResponsibleParty } from '../../../../shared/models/order-entry/reponsible-party';
import { DiagnosisService } from '../../../../shared/services';
import { OrdersService } from '../../../../shared/services/orders.service';
import { OrderBillingInformation, OrderBillingInformationEditable } from '../../models/order-billing-information';
import * as Moment from 'moment';

@Component({
  selector: 'app-missing-info',
  templateUrl: './missing-info.component.html',
  styleUrls: ['./missing-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MissingInfoComponent implements OnInit, OnDestroy {
  @Input() public popupVisible = false;
  @Input() public order: OrderBillingInformation = new OrderBillingInformation();
  @Output() public closeEvent = new EventEmitter();
  // @ts-ignore
  public diagnosisDataSource: BehaviorSubject<DataSource> = new BehaviorSubject<DataSource>(null);
  public missingInfoForm: FormGroup;
  public isLoading = false;
  public today = new Date();
  public gender = ['MALE', 'FEMALE', 'UNKNOWN'];
  public hasMissingDiagnosis = false;
  public hasMissingBillingInformation = false;
  public hasOrderingPhysician = false;
  public isPhone: boolean = false;
  public isPhoneOrTablet: boolean = false;
  public deselectAllBillingInfo = new EventEmitter();
  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    public diagnoseService: DiagnosisService,
    public dateHelper: DateHelperService,
    private ordersService: OrdersService,
    private renderer: Renderer2,
    private elem: ElementRef,
    private screen: ScreenService,
    private fusionAuthService: FusionAuthService,
    private ref: ChangeDetectorRef
  ) {
    this.missingInfoForm = this.fb.group({
      birthDate: new FormControl(null, Validators.required),
      gender: new FormControl(null, Validators.required),
      orderingPhysician: new FormControl(null, Validators.required),
      diagnoses: new FormControl([]),
      insuranceInformation: new FormControl([]),
      responsibleParties: new FormControl([]),
      comment: new FormControl(null),
    });
  }

  public convertToFormControl(absCtrl: AbstractControl | null): FormControl {
    return absCtrl as FormControl;
  }

  public ngOnInit() {
    this.isPhone = this.screen.sizes['screen-x-small'];
    this.isPhoneOrTablet = this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'] || this.screen.sizes['screen-tablet'];
    this.screen.changed.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.isPhone = this.screen.sizes['screen-x-small'];
      this.isPhoneOrTablet = this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small'] || this.screen.sizes['screen-tablet'];
    });

    this.diagnosisDataSource.next(new DataSource({
      store: this.diagnoseService.customStore(this.diagnosisDataSource),
    }));

    this.missingInfoForm.get('diagnoses')?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(diagnoses => {
        this.hasDiagnosis(diagnoses);
        this.ref.detectChanges();
      });
    this.ref.detectChanges();
  }

  public get PatientDate () {
    if (!this.order.patient) return '';
    if (!this.order.patient?.birthDate) return '';
    return this.dateHelper.dateWithFormat(this.order.patient?.birthDate as Date,
      "MM/DD/YYYY");
  }

  public onShown() {
    this.isLoading = true;

    if (this.order !== undefined && this.order !== null) {
      this.missingInfoForm.get('birthDate')?.setValue(this.order?.patient?.birthDate);
      this.missingInfoForm.get('gender')?.setValue(this.order?.patient?.gender);
      this.missingInfoForm.get('orderingPhysician')?.setValue(this.order.physician);
      this.missingInfoForm.get('diagnoses')?.setValue(this.order.diagnoses);
      // @ts-ignore
      this.missingInfoForm.get('insuranceInformation')?.setValue(this.order.patient?.insuranceInformation.sort((a, b) => a.order - b.order));
      this.missingInfoForm.get('responsibleParties')?.setValue(this.order.patient?.responsibleParties);
      this.missingInfoForm.get('comment')?.setValue('<html lang="eng"><head><title></title></head><body></body></html>');

      if (this.order.patient?.birthDate != null) {
        this.missingInfoForm.get('birthDate')?.disable();
      } else {
        this.missingInfoForm.get('birthDate')?.enable();
      }
      if (this.order?.patient?.gender != null && this.order.patient.gender !== '') {
        this.missingInfoForm.get('gender')?.disable();
      } else {
        this.missingInfoForm.get('gender')?.enable();
      }
      if (this.order.orderEntryAlert != null) {
        this.missingInfoForm.get('comment')?.disable();
      } else {
        this.missingInfoForm.get('comment')?.enable();
      }
      this.hasOrderingPhysician = this.missingInfoForm.get('orderingPhysician')?.value !== null;

      this.hasDiagnosis(this.missingInfoForm.get('diagnoses')?.value);
      this.hasBillingInformation(this.missingInfoForm.get('insuranceInformation')?.value);
    }
    this.isLoading = false;
    ValidationEngine.validateGroup('missingInfo');
    this.ref.detectChanges();
  }

  public birthDateChange(e: any) {
    if (this.order.patient)
      this.order.patient.birthDate = e.value;
    this.ref.detectChanges();
  }

  public genderChange(e: any) {
    if (this.order.patient)
      this.order.patient.gender = e.value;
    this.ref.detectChanges();
  }

  public billingInfoChanged(billingInfo: BillingInformation[]) {
    this.missingInfoForm.get('insuranceInformation')?.setValue(billingInfo);
    this.hasBillingInformation(this.missingInfoForm.get('insuranceInformation')?.value);
    this.ref.detectChanges();
  }

  public responsiblePartiesChanged(responsibleParties: ResponsibleParty[]) {
    this.missingInfoForm.get('responsibleParties')?.setValue(responsibleParties);
    this.ref.detectChanges();
  }

  public hasDiagnosis(diagnoses: number[] = []) {
    this.hasMissingDiagnosis = diagnoses.length === 0;
  }

  public hasBillingInformation(billingInfo: BillingInformation[] = []) {
    const some = billingInfo.length === 0 || billingInfo.filter((x: BillingInformation) => {
      return !x.lastName || x.lastName === '' || !x.firstName || x.firstName === '' || !x.birthDate ||
      !x.address || x.address === '' || !x.zipCode || x.id === 0 || !(x.zipCode as ZipCode).city || !(x.zipCode as ZipCode).cityId ||
        !(x.zipCode as ZipCode).state || !(x.zipCode as ZipCode).stateId || !x.gender || x.gender === '' || !x.relationship ||
        x.relationship === '' || !x.insuranceId || x.insuranceId === ''
    }).length > 0;
    this.hasMissingBillingInformation = some;
    this.ref.detectChanges();
  }

  public close(): void {
    this.missingInfoForm.get('birthDate')?.setValue(null);
    this.missingInfoForm.get('gender')?.setValue(null);
    this.missingInfoForm.get('orderingPhysician')?.setValue(null);
    this.missingInfoForm.get('comment')?.setValue(null);
    this.missingInfoForm.get('diagnoses')?.setValue([]);
    this.missingInfoForm.get('insuranceInformation')?.setValue([]);
    this.missingInfoForm.get('responsibleParties')?.setValue([]);
    this.closeEvent.emit(true);
  }

  public submit() {
    this.isLoading = true;
    const user = this.fusionAuthService.Profile;

    const orderBillingInformation = new OrderBillingInformationEditable();

    orderBillingInformation.orderId = this.order.id;
    orderBillingInformation.patientId = this.order.patient?.id;
    orderBillingInformation.birthDate = this.missingInfoForm.get('birthDate')?.value;
    orderBillingInformation.gender = this.missingInfoForm.get('gender')?.value;
    orderBillingInformation.physicianId = this.missingInfoForm.get('orderingPhysician')?.value ?
      this.missingInfoForm.get('orderingPhysician')?.value.id : 0;
    orderBillingInformation.diagnoses = this.missingInfoForm.get('diagnoses')?.value;
    orderBillingInformation.insuranceInformation = this.missingInfoForm.get('insuranceInformation')?.value;
    orderBillingInformation.responsibleParties = this.missingInfoForm.get('responsibleParties')?.value;
    orderBillingInformation.username = user.lastName + ', ' + user.firstName;
    orderBillingInformation.comments = (this.missingInfoForm.get('comment')?.value === null || this.missingInfoForm.get('comment')?.value === '' ||
      this.missingInfoForm.get('comment')?.value.indexOf('<p>') === -1) ? '' : this.missingInfoForm.get('comment')?.value;

    for (const rp of orderBillingInformation.responsibleParties ? orderBillingInformation.responsibleParties : []) {
      rp.zipCodeName = typeof rp.zipCode === 'string' ? rp.zipCode : rp.zipCode.code;
      rp.address = rp.address ? rp.address.toUpperCase() : '';
    }

    for (const bi of orderBillingInformation.insuranceInformation ? orderBillingInformation.insuranceInformation : []) {
      bi.zipCodeName = typeof bi.zipCode === 'string' ? bi.zipCode : bi.zipCode?.code;
     /* if (bi.relationship === 'SELF') {
        bi.zipCodeName = this.order?.patient?.zipCodeName;
        bi.address = this.order.patient?.address;
        bi.birthDate = this.order.patient?.birthDate ? this.order.patient?.birthDate : '';
        bi.firstName = this.order.patient?.firstName ? this.order.patient?.firstName.toUpperCase() : '';
        bi.lastName = this.order.patient?.lastName ? this.order.patient?.lastName.toUpperCase() : '';
        bi.gender = this.order.patient?.gender;
        bi.middleName = this.order.patient?.middleName ? this.order.patient?.middleName.toUpperCase() : '';
      }*/
    }

    this.ordersService.updateMissingInfo(this.order.id, orderBillingInformation)
      .pipe(take(1))
      .subscribe(() => {
        this.isLoading = false;
        this.deselectAllBillingInfo.emit();
        this.missingInfoForm.reset({
          birthDate: null,
          gender: null,
          orderingPhysician: null,
          diagnoses: [],
          insuranceInformation: [],
          responsibleParties: [],
          comment: null,
        });
        this.closeEvent.emit(false);
      });
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
